import React, { useMemo } from 'react';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Tag from '@amzn/meridian/tag';
import { TEXTS } from '../../utils/constants';
import OpportunityBar from './OpportunityBar';
import { isoToEpochSec } from './LaborAvailabilityChartModel';
import { TYPE_TO_TAG } from '../../utils/laborOpportunityConstants';
import './shared/flowPlannerShared.css';

const opportunityRange = (opportunity) => {
  const tw = opportunity.applicability?.timeWindow;
  if (!tw?.start || !tw?.end) return null;
  const start = isoToEpochSec(tw.start);
  const end = isoToEpochSec(tw.end);
  return end > start ? { start, end } : null;
};

const sortByStart = (a, b) => a.range.start - b.range.start || a.range.end - b.range.end;
const percent = (value, span) => `${(value / span) * 100}%`;

const buildIntervalShading = (intervals, statuses, axisStart, span) => {
  if (!statuses?.length) return [];
  return intervals
    .map((iv, idx) => {
      const status = statuses[idx];
      if (!status) return null;
      return {
        leftPct: percent(iv.startTime - axisStart, span),
        widthPct: percent(iv.endTime - iv.startTime, span),
        status
      };
    })
    .filter(Boolean);
};

/**
 * Per-opportunity Gantt-style timeline aligned to the planning intervals.
 * Tracks shade by `intervalStatuses` (computed once by the parent so the
 * Recommendations table and this view never disagree); each opportunity gets
 * one bar whose hover popover reveals time window, headcount, and affected
 * work groups.
 */
const OpportunityTimeline = ({ laborOpportunities, intervals, intervalStatuses }) => {
  const axis = useMemo(() => {
    if (!intervals?.length) return null;
    const start = intervals[0].startTime;
    const end = intervals[intervals.length - 1].endTime;
    const span = end - start;
    if (span <= 0) return null;
    const ticks = [
      { epoch: start, label: intervals[0].startLabel },
      ...intervals.map((iv) => ({ epoch: iv.endTime, label: iv.endLabel }))
    ];
    return { start, end, span, ticks };
  }, [intervals]);

  const tracks = useMemo(() => {
    if (!axis) return [];
    return (laborOpportunities || [])
      .map((opportunity) => {
        const range = opportunityRange(opportunity);
        if (!range) return null;
        const clippedStart = Math.max(range.start, axis.start);
        const clippedEnd = Math.min(range.end, axis.end);
        if (clippedEnd <= clippedStart) return null;
        return { opportunity, range: { start: clippedStart, end: clippedEnd } };
      })
      .filter(Boolean)
      .sort(sortByStart);
  }, [laborOpportunities, axis]);

  const shading = useMemo(
    () => (axis ? buildIntervalShading(intervals, intervalStatuses, axis.start, axis.span) : []),
    [axis, intervals, intervalStatuses]
  );

  if (!axis || !tracks.length) return null;

  const { start: axisStart, span, ticks } = axis;

  return (
    <Column spacing="400" alignmentHorizontal="stretch" width="100%">
      <Row spacing="300" alignmentVertical="center" alignmentHorizontal="justify">
        <Text type={TEXTS.T3} color="secondary">
          Opportunity Timeline
        </Text>
        <Row spacing="300" alignmentVertical="center">
          <Row spacing="100" alignmentVertical="center">
            <span className="opportunity-timeline-legend-dot" data-status="shortage" />
            <Text type={TEXTS.T3} color="secondary">
              Shortage
            </Text>
          </Row>
          <Row spacing="100" alignmentVertical="center">
            <span className="opportunity-timeline-legend-dot" data-status="surplus" />
            <Text type={TEXTS.T3} color="secondary">
              Surplus
            </Text>
          </Row>
        </Row>
      </Row>
      {tracks.map(({ opportunity, range }, idx) => {
        const leftPct = percent(range.start - axisStart, span);
        const widthValue = ((range.end - range.start) / span) * 100;
        const widthPct = `${widthValue}%`;
        return (
          <div key={idx} className="opportunity-timeline-row">
            <div className="opportunity-timeline-cell">
              <Tag type={TYPE_TO_TAG[opportunity.opportunityType] || 'neutral'}>{opportunity.opportunityType}</Tag>
            </div>
            <div className="opportunity-timeline-track">
              {shading.map((s, sIdx) => (
                <div
                  key={`shade-${sIdx}`}
                  className="opportunity-timeline-shading"
                  data-status={s.status}
                  style={{ '--shade-left': s.leftPct, '--shade-width': s.widthPct }}
                />
              ))}
              {ticks.map((tick, tIdx) => (
                <div
                  key={`tick-${tIdx}`}
                  className="opportunity-timeline-guide"
                  style={{ '--guide-left': percent(tick.epoch - axisStart, span) }}
                />
              ))}
              <OpportunityBar
                opportunity={opportunity}
                range={range}
                leftPct={leftPct}
                widthPct={widthPct}
                widthValue={widthValue}
              />
            </div>
          </div>
        );
      })}
      <div className="opportunity-timeline-row">
        <div />
        <div className="opportunity-timeline-axis-row">
          {ticks.map((tick, tIdx) => {
            const edge = tIdx === 0 ? 'start' : tIdx === ticks.length - 1 ? 'end' : undefined;
            return (
              <span
                key={tIdx}
                className="opportunity-timeline-axis-tick"
                data-edge={edge}
                style={{ '--tick-left': percent(tick.epoch - axisStart, span) }}>
                {tick.label}
              </span>
            );
          })}
        </div>
      </div>
    </Column>
  );
};

export default OpportunityTimeline;
